import { motion, useReducedMotion } from "framer-motion";

const BRANCH = {
    grow: "M320 132 C 320 196, 110 176, 110 262",
    recover: "M320 132 C 320 180, 320 190, 320 262",
    reduce: "M320 132 C 320 196, 530 176, 530 262",
};

const MERGE = {
    grow: "M110 470 C 110 556, 320 512, 320 596",
    recover: "M320 478 L 320 596",
    reduce: "M530 470 C 530 556, 320 512, 320 596",
};

const CAPS = {
    grow: ["Account Intelligence", "Qualified Demand", "Conversion", "Sales Productivity"],
    recover: ["Dormant Customers", "Lost Opportunities", "Expired Quotes", "Old Leads", "Cross-Sell", "Renewals"],
    reduce: ["Vendor Costs", "Margin Leakage", "Admin Waste", "Pricing Inefficiencies"],
};

const NODE_META = {
    grow: { label: "GROW", sub: "NEW REVENUE", color: "#1E88F7" },
    recover: { label: "RECOVER", sub: "EXISTING REVENUE", color: "#3977F6" },
    reduce: { label: "REDUCE", sub: "OPERATING COST", color: "#0B1220" },
};

const FlowDot = ({ path, color, dur = 3, begin = 0 }) => (
    <circle r="3.5" fill={color}>
        <animateMotion dur={`${dur}s`} begin={`${begin}s`} repeatCount="indefinite" path={path} />
    </circle>
);

const Node = ({ x, id, delay }) => {
    const m = NODE_META[id];
    return (
        <motion.g initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}>
            <rect x={x - 75} y="262" width="150" height="60" rx="4" fill="#FFFFFF" stroke={id === "reduce" ? "rgba(11,18,32,0.35)" : m.color} strokeWidth="1.5" />
            <rect x={x - 75} y="262" width="150" height="3" fill={m.color} />
            <text x={x} y="290" textAnchor="middle" fontSize="15" fontWeight="800" fill="#0B1220" fontFamily="Manrope, sans-serif" letterSpacing="1">{m.label}</text>
            <text x={x} y="308" textAnchor="middle" fontSize="8.5" fill="#4A5568" fontFamily="IBM Plex Mono, monospace" letterSpacing="1.5">{m.sub}</text>
        </motion.g>
    );
};

const ProfitEngine = () => {
    const reduceMotion = useReducedMotion();
    return (
        <div className="relative" data-testid="profit-engine-visual">
            <svg viewBox="0 0 640 760" className="w-full h-auto" role="img" aria-label="Vantera Profit Engine: business feeding grow, recover and reduce streams that converge into profit">
                <defs>
                    <linearGradient id="pe-scan" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0" stopColor="#1E88F7" stopOpacity="0" />
                        <stop offset="0.5" stopColor="#1E88F7" stopOpacity="0.8" />
                        <stop offset="1" stopColor="#1E88F7" stopOpacity="0" />
                    </linearGradient>
                </defs>

                {/* X-ray scan line */}
                {!reduceMotion && (
                    <motion.line x1="30" x2="610" stroke="url(#pe-scan)" strokeWidth="1.5" initial={{ y1: 60, y2: 60 }} animate={{ y1: [60, 700], y2: [60, 700] }} transition={{ duration: 6, repeat: Infinity, ease: "linear", repeatDelay: 1.2 }} />
                )}

                {/* corner crosshairs */}
                {[[24, 24], [616, 24], [24, 736], [616, 736]].map(([x, y], i) => (
                    <g key={i} stroke="rgba(11,18,32,0.25)" strokeWidth="1">
                        <line x1={x - 7} x2={x + 7} y1={y} y2={y} />
                        <line x1={x} x2={x} y1={y - 7} y2={y + 7} />
                    </g>
                ))}

                {/* BUSINESS node */}
                <motion.g initial={{ opacity: 0, scale: 0.94 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.7 }} style={{ transformOrigin: "320px 90px" }}>
                    <rect x="230" y="62" width="180" height="56" rx="4" fill="#0B1220" />
                    <text x="320" y="96" textAnchor="middle" fontSize="16" fontWeight="800" fill="#F7F8F6" fontFamily="Manrope, sans-serif" letterSpacing="3">BUSINESS</text>
                    <circle cx="320" cy="122" r="3" fill="#1E88F7" className="animate-blink" />
                </motion.g>

                {/* branch paths */}
                {Object.entries(BRANCH).map(([id, d], i) => (
                    <g key={id}>
                        <motion.path d={d} fill="none" stroke="rgba(11,18,32,0.18)" strokeWidth="1.5" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 1.1, delay: 0.3 + i * 0.2, ease: "easeOut" }} />
                        <path d={d} fill="none" stroke={NODE_META[id].color} strokeWidth="1.5" strokeDasharray="3 9" opacity="0.55" className="animate-dash-flow" />
                        {!reduceMotion && <FlowDot path={d} color={NODE_META[id].color} begin={i * 1} dur={2.8} />}
                    </g>
                ))}

                <Node x={110} id="grow" delay={0.5} />
                <Node x={320} id="recover" delay={0.65} />
                <Node x={530} id="reduce" delay={0.8} />

                {/* capabilities */}
                {Object.entries(CAPS).map(([id, caps], i) => (
                    <motion.g key={id} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 1 + i * 0.15 }} className="hidden sm:block">
                        {caps.map((c, j) => (
                            <text key={j} x={id === "grow" ? 110 : id === "recover" ? 320 : 530} y={356 + j * 19} textAnchor="middle" fontSize="10" fill="#4A5568" fontFamily="IBM Plex Mono, monospace" letterSpacing="0.5">{c.toUpperCase()}</text>
                        ))}
                    </motion.g>
                ))}

                {/* converge paths */}
                {Object.entries(MERGE).map(([id, d], i) => (
                    <g key={id}>
                        <motion.path d={d} fill="none" stroke="rgba(11,18,32,0.18)" strokeWidth="1.5" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 1.1, delay: 1.2 + i * 0.18, ease: "easeOut" }} />
                        <path d={d} fill="none" stroke="#1E88F7" strokeWidth="1.5" strokeDasharray="3 9" opacity="0.5" className="animate-dash-flow" />
                        {!reduceMotion && <FlowDot path={d} color="#1E88F7" begin={1.2 + i * 0.9} dur={3} />}
                    </g>
                ))}

                {/* PROFIT node */}
                <motion.g initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 1.7, ease: [0.16, 1, 0.3, 1] }} style={{ transformOrigin: "320px 636px" }}>
                    {!reduceMotion && (
                        <motion.circle cx="320" cy="636" r="46" fill="none" stroke="#1E88F7" strokeWidth="1.5" animate={{ r: [46, 84], opacity: [0.5, 0] }} transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }} />
                    )}
                    <rect x="200" y="596" width="240" height="80" rx="4" fill="rgba(30,136,247,0.1)" stroke="#1E88F7" strokeWidth="1.5" />
                    <text x="320" y="634" textAnchor="middle" fontSize="24" fontWeight="800" fill="#0B1220" fontFamily="Manrope, sans-serif" letterSpacing="4">PROFIT</text>
                    <text x="320" y="656" textAnchor="middle" fontSize="9" fill="#0F6FE0" fontFamily="IBM Plex Mono, monospace" letterSpacing="2">MORE PROFIT</text>
                </motion.g>

                {/* floating metric labels */}
                {[
                    { x: 62, y: 186, t: "+ NEW REVENUE", c: "#0F6FE0", d: 0 },
                    { x: 352, y: 176, t: "$ RECOVERED", c: "#3977F6", d: 1.6 },
                    { x: 428, y: 186, t: "− OPERATING COST", c: "#0B1220", d: 3.1 },
                ].map((m, i) => (
                    <motion.text key={i} x={m.x} y={m.y} fontSize="10" fill={m.c} fontFamily="IBM Plex Mono, monospace" letterSpacing="1.5" initial={{ opacity: 0 }} animate={reduceMotion ? { opacity: 1 } : { opacity: [0, 1, 1, 0] }} transition={reduceMotion ? { delay: 2 } : { duration: 5, delay: m.d, repeat: Infinity, repeatDelay: 2.5 }}>
                        {m.t}
                    </motion.text>
                ))}
            </svg>
        </div>
    );
};

export default ProfitEngine;